import { type QueryKey, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef } from "react";
import { useEventSubscription } from "@/hooks/useEventSubscription";
import { type AttentionQueueData, applyAttentionPatch } from "@/lib/attention-queue";
import { queryKeys } from "@/lib/query-keys";
import type { ConversationPatch } from "@/types/chat";

/** Events arrive per message. A burst becomes one refetch at most this often,
 *  and the first event of a burst is never held back longer than this. */
const REFRESH_THROTTLE_MS = 2000;

/**
 * Keeps the app-wide query caches in step with the event stream, from one
 * subscription instead of one per component that reads them:
 *
 * - the needs-a-person queue (sidebar badge, home, empty inbox), patched in
 *   place when the event says enough and refetched when it doesn't;
 * - channel health, whenever a channel changes;
 * - order lists, whenever the bot posts, since its orders and payment
 *   requests arrive as bot messages with no order event of their own.
 *
 * Mounted once in `ProtectedShell`, inside the event stream.
 */
export function LiveQuerySync(): null {
  const queryClient = useQueryClient();
  const timers = useRef(new Map<string, number>());

  const refreshSoon = useCallback(
    (queryKey: QueryKey) => {
      const id = JSON.stringify(queryKey);
      if (timers.current.has(id)) return;
      timers.current.set(
        id,
        window.setTimeout(() => {
          timers.current.delete(id);
          void queryClient.invalidateQueries({ queryKey });
        }, REFRESH_THROTTLE_MS),
      );
    },
    [queryClient],
  );

  useEffect(() => {
    const pending = timers.current;
    return () => {
      for (const timer of pending.values()) window.clearTimeout(timer);
      pending.clear();
    };
  }, []);

  function patchAttention(patch: ConversationPatch) {
    const data = queryClient.getQueryData<AttentionQueueData>(queryKeys.conversations.attention);
    // Nothing read yet: the first reader fetches fresh anyway.
    if (!data) return;
    const result = applyAttentionPatch(data, patch);
    if (result.kind === "updated") queryClient.setQueryData(queryKeys.conversations.attention, result.data);
    else if (result.kind === "refetch") refreshSoon(queryKeys.conversations.attention);
  }

  useEventSubscription({
    onMessageNew: ({ sidebar, message }) => {
      patchAttention(sidebar);
      if (message.sender === "system") refreshSoon(queryKeys.orders.lists);
    },
    onConversationChanged: (_conversationId, patch) => {
      if (patch) patchAttention(patch);
      else refreshSoon(queryKeys.conversations.attention);
    },
    onChannelChanged: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.channels.all });
    },
    onReconnect: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.conversations.attention });
      void queryClient.invalidateQueries({ queryKey: queryKeys.channels.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.orders.lists });
    },
  });

  return null;
}
